import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, ComposedChart } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { format } from 'date-fns';
import { useRatingHistory } from '@/hooks/use-rating-history';
import { useLanguage } from '@/hooks/use-language';

interface RatingHistoryChartProps {
  playerId?: string;
  height?: number;
}

const CustomTooltip = ({ active, payload }: any) => {
  if (!active || !payload || !payload.length) return null;

  const data = payload[0].payload;
  const ratingChange = data.ratingAfter - data.ratingBefore;

  return (
    <div className="bg-card border border-border rounded-lg shadow-lg p-3 space-y-2">
      <p className="text-sm font-medium text-foreground">
        {format(new Date(data.date), 'PPP')}
      </p>
      <div className="space-y-1">
        <div className="flex items-center justify-between gap-3">
          <span className="text-xs text-muted-foreground">Rating:</span>
          <span className="text-sm font-mono font-medium">{Math.round(data.rating)}</span>
        </div>
        <div className="flex items-center justify-between gap-3">
          <span className="text-xs text-muted-foreground">Change:</span>
          <span className={`text-sm font-mono font-medium flex items-center gap-1 ${
            ratingChange > 0 ? 'text-green-600' : ratingChange < 0 ? 'text-red-600' : 'text-muted-foreground'
          }`}>
            {ratingChange > 0 ? <TrendingUp className="h-3 w-3" /> : ratingChange < 0 ? <TrendingDown className="h-3 w-3" /> : <Minus className="h-3 w-3" />}
            {ratingChange > 0 ? '+' : ''}{Math.round(ratingChange)}
          </span>
        </div>
        <div className="flex items-center justify-between gap-3">
          <span className="text-xs text-muted-foreground">Confidence:</span>
          <span className="text-xs font-mono">±{Math.round(1.96 * data.deviation)}</span>
        </div>
        <div className="flex items-center justify-between gap-3">
          <span className="text-xs text-muted-foreground">Reliability:</span>
          <span className="text-xs font-mono">{(data.reliability * 100).toFixed(1)}%</span>
        </div>
      </div>
    </div>
  );
};

export function RatingHistoryChart({ playerId, height = 400 }: RatingHistoryChartProps) {
  const { data: history, isLoading } = useRatingHistory(playerId);
  const { t } = useLanguage();

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            {t('rating.history')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="w-full" style={{ height }} />
        </CardContent>
      </Card>
    );
  }

  if (!history || history.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            {t('rating.history')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-12 text-center" style={{ height }}>
            <TrendingUp className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">{t('rating.history.no_data')}</p>
            <p className="text-sm text-muted-foreground mt-2">
              {t('rating.history.play_matches')}
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const chartData = history.map((point) => ({
    ...point,
    dateFormatted: format(new Date(point.date), 'MMM d'),
  }));

  const minRating = Math.min(...history.map(h => h.lowerBound));
  const maxRating = Math.max(...history.map(h => h.upperBound));
  const padding = (maxRating - minRating) * 0.1;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <TrendingUp className="h-5 w-5" />
          {t('rating.history')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={height}>
          <ComposedChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
            <XAxis 
              dataKey="dateFormatted" 
              className="text-xs"
              stroke="hsl(var(--muted-foreground))"
            />
            <YAxis 
              domain={[Math.floor(minRating - padding), Math.ceil(maxRating + padding)]}
              className="text-xs"
              stroke="hsl(var(--muted-foreground))"
            />
            <Tooltip content={<CustomTooltip />} />
            
            {/* Confidence interval area */}
            <Area
              type="monotone"
              dataKey="upperBound"
              stroke="none"
              fill="hsl(var(--primary))"
              fillOpacity={0.1}
            />
            <Area
              type="monotone"
              dataKey="lowerBound"
              stroke="none"
              fill="hsl(var(--primary))"
              fillOpacity={0.1}
            />
            
            {/* Main rating line */}
            <Line
              type="monotone"
              dataKey="rating"
              stroke="hsl(var(--primary))"
              strokeWidth={3}
              dot={{ fill: 'hsl(var(--primary))', strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6 }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
